import router from 'next/router'
import { Dispatch, SetStateAction, useEffect, useState, VFC } from 'react'
import { MasterDataStore } from 'src/api/masterData'
import { CompanySearchConditionsData } from 'src/api/types/needs'
import {
  NeedsAuthorizerIcon,
  NeedsAuthorizerXIcon,
  NeedsFreeSearchIcon,
  NeedsSavedSearchIcon,
  NeedsSavedSearchXIcon,
  NeedsSearchIcon,
  TrashIcon,
} from 'src/assets/svgs'
import { TooltipWrapper } from 'src/components/Tooltip'
import { Types } from 'src/compositions/Profile/Persona/form'
import { LargeButton } from 'src/elements/Button'
import {
  MasterData,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import {
  toNeedsQueryParamFromCompanySearchConditionsDataParam,
  toPersona,
} from 'src/models/Needs'
import { Persona } from 'src/models/Persona/index'
import { LoginUser } from 'src/models/User'
import { orange, safetyOrange, truewhite } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { KEEP } from 'src/utils/routes'
import styled, { css } from 'styled-components'
import { PersonaBoxComponent } from '../Profile/Persona/common'
import { PersonasContent } from './PersonasContent'
import { PersonasContentModal } from './PersonasContentModal'

type CompanyPersonasFormProps = {
  user: LoginUser
  masterData: MasterDataStore
  personaData: Persona
  setPersonaData: Dispatch<SetStateAction<Persona>>
  searchNeeds: (hasFreeWord: boolean) => void
  searchCompanySearchConditionsData?: CompanySearchConditionsData[]
  deleteCompanySearchConditions: (id: number) => void
  personaDataList: Persona[] | undefined
}

export const CompanyPersonasForm: VFC<CompanyPersonasFormProps> = ({
  user,
  masterData,
  personaData,
  setPersonaData,
  searchNeeds,
  searchCompanySearchConditionsData,
  deleteCompanySearchConditions,
  personaDataList,
}) => {
  const [selectedType, setSelectedType] = useState<Types>('businessTypes')
  const [checkedBusinessTypes, setCheckedBusinessTypes] = useState<
    MasterData[]
  >(
    masterData.businessTypes!.filter(({ value }) =>
      personaData.personaTypeOfBusinessIds.includes(Number(value)),
    ),
  )
  const [checkedNumberOfEmployees, setCheckedNumberOfEmployees] = useState<
    MasterData[]
  >(
    masterData.numberOfEmployees!.filter(({ value }) =>
      personaData.personaEmployeeNumberIds.includes(Number(value)),
    ),
  )
  const [checkedNeeds, setCheckedNeeds] = useState<MasterData[]>(
    masterData.needs!.filter(({ value }) =>
      personaData.personaCompanyBuyNeedOptionIds.includes(Number(value)),
    ),
  )
  const [checkedSolutions, setCheckedSolutions] = useState<MasterData[]>(
    masterData.solutions!.filter(({ value }) =>
      personaData.personaSolutionIds.includes(Number(value)),
    ),
  )
  const [checkedPrefectures, setCheckedPrefectures] = useState<
    MasterDataWithRegionalDivisionType[]
  >(
    masterData.prefectures!.filter(({ value }) =>
      personaData.personaPrefectureIds.includes(Number(value)),
    ),
  )
  const [checkedPositionNumberOptions, setCheckedPositionNumberOptions] =
    useState<MasterData[]>(
      masterData.positionNumberOptions!.filter(({ value }) =>
        personaData.personaPositionIds.includes(Number(value)),
      ),
    )
  const [checkedCapitalNumberOptions, setCheckedCapitalNumberOptions] =
    useState<MasterData[]>(
      masterData.capitalNumberOptions!.filter(({ value }) =>
        personaData.personaCapitalIds.includes(Number(value)),
      ),
    )
  const [checkedBusinessObjects, setCheckedBusinessObjects] = useState<
    MasterData[]
  >(
    masterData.businessObjects!.filter(({ value }) =>
      personaData.personaBusinessObjectIds.includes(Number(value)),
    ),
  )
  const [checkedFoundedAts, setCheckedFoundedAts] = useState<MasterData[]>(
    masterData.foundedAts!.filter(({ value }) =>
      personaData.personaFoundedAtIds.includes(Number(value)),
    ),
  )
  const [checkedAdminAges, setCheckedAdminAges] = useState<MasterData[]>(
    masterData.adminAges!.filter(({ value }) =>
      personaData.personaAdminAgeIds.includes(Number(value)),
    ),
  )

  const masterDataRoleForAnalysis: MasterData[] = [
    {
      label: '無料',
      value: 'free',
    },
    {
      label: '有料',
      value: 'paid',
    },
  ]
  const [checkedRoleForAnalysis, setCheckedRoleForAnalysis] = useState<
    MasterData[]
  >(
    masterDataRoleForAnalysis.filter(({ value }) =>
      personaData.personaRoleForAnalysisIds?.includes(value),
    ),
  )

  const [minCreatedDate, setMinCreatedDate] = useState<string>(
    personaData?.minCreatedDate ?? '',
  )
  const [maxCreatedDate, setMaxCreatedDate] = useState<string>(
    personaData?.maxCreatedDate ?? '',
  )
  const [minLastSignInAt, setMinLastSignInAt] = useState<string>(
    personaData?.minLastSignInAt ?? '',
  )
  const [maxLastSignInAt, setMaxLastSignInAt] = useState<string>(
    personaData?.maxLastSignInAt ?? '',
  )

  const [isDisplayForm, setIsDisplayForm] = useState<boolean>(true)
  const [showPersonasContentModal, setShowPersonasContentModal] =
    useState<boolean>(false)
  const [selectedPersonaData, setSelectedPersonaData] = useState<Persona>({
    id: 0,
    personaAdminAgeIds: [],
    personaBusinessObjectIds: [],
    personaEmployeeNumberIds: [],
    personaFoundedAtIds: [],
    personaPrefectureIds: [],
    personaTypeOfBusinessIds: [],
    personaCompanyBuyNeedOptionIds: [],
    personaSolutionIds: [],
    personaCapitalIds: [],
    personaPositionIds: [],
    personaRoleForAnalysisIds: [],
    refuseMessage: false,
    noMessage: false,
    isNew: false,
    isListedCompany: null,
    freeWord: null,
    minCreatedDate: null,
    maxCreatedDate: null,
    minLastSignInAt: null,
    maxLastSignInAt: null,
    personaAdminAges: [],
    personaCompanyBuyNeedOptions: [],
    personaSolutions: [],
    personaBusinessObjects: [],
    personaEmployeeNumbers: [],
    personaFoundedAts: [],
    personaPrefectures: [],
    personaTypeOfBusinesses: [],
    personaCapitals: [],
    personaPositions: [],
    personaRoleForAnalysis: [],
  })

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.which === 13) onClickSearchNeeds(true)
  }

  const onClickSearchCompanySearchConditionsData = async (
    item: CompanySearchConditionsData,
  ) => {
    const needsQueryParam =
      toNeedsQueryParamFromCompanySearchConditionsDataParam(item.params)
    const persona = toPersona(needsQueryParam, masterData)
    setSelectedPersonaData(persona)
    setShowPersonasContentModal(true)
  }

  const onClickPersonaData = async (persona: Persona) => {
    setSelectedPersonaData(persona)
    setShowPersonasContentModal(true)
  }

  const setSearchCompanySearchConditionsData = async (
    item: CompanySearchConditionsData,
  ) => {
    const needsQueryParam =
      toNeedsQueryParamFromCompanySearchConditionsDataParam(item.params)
    const persona = toPersona(needsQueryParam, masterData)
    setPersonaData(persona)
  }

  const deleteSearchCompanySearchConditionsData = async (
    item: CompanySearchConditionsData,
  ) => {
    await deleteCompanySearchConditions(item.id)
  }

  const deselectAll = () => {
    setCheckedBusinessTypes([])
    setCheckedNumberOfEmployees([])
    setCheckedNeeds([])
    setCheckedSolutions([])
    setCheckedPrefectures([])
    setCheckedPositionNumberOptions([])
    setCheckedCapitalNumberOptions([])
    setCheckedFoundedAts([])
    setCheckedBusinessObjects([])
    setCheckedAdminAges([])
    setCheckedRoleForAnalysis([])
    setMinCreatedDate('')
    setMaxCreatedDate('')
    setMinLastSignInAt('')
    setMaxLastSignInAt('')
    setPersonaData({
      id: 0,
      personaAdminAgeIds: [],
      personaBusinessObjectIds: [],
      personaEmployeeNumberIds: [],
      personaFoundedAtIds: [],
      personaPrefectureIds: [],
      personaTypeOfBusinessIds: [],
      personaCompanyBuyNeedOptionIds: [],
      personaSolutionIds: [],
      personaCapitalIds: [],
      personaPositionIds: [],
      personaRoleForAnalysisIds: [],
      refuseMessage: personaData.refuseMessage,
      noMessage: personaData.noMessage,
      isNew: personaData.isNew,
      isListedCompany: null,
      freeWord: personaData.freeWord,
      minCreatedDate: null,
      maxCreatedDate: null,
      minLastSignInAt: null,
      maxLastSignInAt: null,
      personaAdminAges: [],
      personaCompanyBuyNeedOptions: [],
      personaSolutions: [],
      personaBusinessObjects: [],
      personaEmployeeNumbers: [],
      personaFoundedAts: [],
      personaPrefectures: [],
      personaTypeOfBusinesses: [],
      personaCapitals: [],
      personaPositions: [],
      personaRoleForAnalysis: [],
    })
  }
  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaTypeOfBusinessIds: checkedBusinessTypes
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaTypeOfBusinesses: checkedBusinessTypes
        .map(({ value, label }) => {
          return {
            id: 0,
            typeOfBusinessId: Number(value),
            typeOfBusiness: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.typeOfBusinessId - b.typeOfBusinessId),
    })
  }, [checkedBusinessTypes])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaEmployeeNumberIds: checkedNumberOfEmployees
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaEmployeeNumbers: checkedNumberOfEmployees
        .map(({ value, label }) => {
          return {
            id: 0,
            employeeNumberId: Number(value),
            employeeNumber: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.employeeNumberId - b.employeeNumberId),
    })
  }, [checkedNumberOfEmployees])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaCompanyBuyNeedOptionIds: checkedNeeds
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaCompanyBuyNeedOptions: checkedNeeds
        .map(({ value, label }) => {
          return {
            id: 0,
            companyBuyNeedOptionId: Number(value),
            companyBuyNeedOption: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.companyBuyNeedOptionId - b.companyBuyNeedOptionId),
    })
  }, [checkedNeeds])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaSolutionIds: checkedSolutions
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaSolutions: checkedSolutions
        .map(({ value, label }) => {
          return {
            id: 0,
            solutionId: Number(value),
            solution: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.solutionId - b.solutionId),
    })
  }, [checkedSolutions])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaPrefectureIds: checkedPrefectures
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaPrefectures: checkedPrefectures
        .map(({ value, label }) => {
          return {
            id: 0,
            prefectureId: Number(value),
            prefecture: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.prefectureId - b.prefectureId),
    })
  }, [checkedPrefectures])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaPositionIds: checkedPositionNumberOptions
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaPositions: checkedPositionNumberOptions
        .map(({ value, label }) => {
          return {
            id: Number(value),
            name: label,
          }
        })
        .sort((a, b) => a.id - b.id),
    })
  }, [checkedPositionNumberOptions])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaCapitalIds: checkedCapitalNumberOptions
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaCapitals: checkedCapitalNumberOptions
        .map(({ value, label }) => {
          return {
            id: Number(value),
            name: label,
          }
        })
        .sort((a, b) => a.id - b.id),
    })
  }, [checkedCapitalNumberOptions])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaBusinessObjectIds: checkedBusinessObjects
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaBusinessObjects: checkedBusinessObjects
        .map(({ value, label }) => {
          return {
            id: 0,
            businessObjectId: Number(value),
            businessObject: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.businessObjectId - b.businessObjectId),
    })
  }, [checkedBusinessObjects])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaFoundedAtIds: checkedFoundedAts
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaFoundedAts: checkedFoundedAts
        .map(({ value, label }) => {
          return {
            id: 0,
            foundedAtId: Number(value),
            foundedAt: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.foundedAtId - b.foundedAtId),
    })
  }, [checkedFoundedAts])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaAdminAgeIds: checkedAdminAges
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaAdminAges: checkedAdminAges
        .map(({ value, label }) => {
          return {
            id: 0,
            adminAgeId: Number(value),
            adminAge: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.adminAgeId - b.adminAgeId),
    })
  }, [checkedAdminAges])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaAdminAgeIds: checkedAdminAges
        .map(({ value }) => Number(value))
        .sort((a, b) => a - b),
      personaAdminAges: checkedAdminAges
        .map(({ value, label }) => {
          return {
            id: 0,
            adminAgeId: Number(value),
            adminAge: {
              id: Number(value),
              name: label,
            },
          }
        })
        .sort((a, b) => a.adminAgeId - b.adminAgeId),
    })
  }, [checkedAdminAges])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      personaRoleForAnalysisIds: checkedRoleForAnalysis
        .map(({ value }) => value)
        .sort((a, b) => (a > b ? 1 : -1)),
      personaRoleForAnalysis: checkedRoleForAnalysis
        .map(({ value, label }) => {
          return {
            id: 0,
            roleForAnalysisId: value,
            roleForAnalysis: {
              id: value,
              name: label,
            },
          }
        })
        .sort((a, b) => (a.roleForAnalysisId > b.roleForAnalysisId ? 1 : -1)),
    })
  }, [checkedRoleForAnalysis])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      minCreatedDate: minCreatedDate,
    })
  }, [minCreatedDate])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      maxCreatedDate: maxCreatedDate,
    })
  }, [maxCreatedDate])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      minLastSignInAt: minLastSignInAt,
    })
  }, [minLastSignInAt])

  useEffect(() => {
    setPersonaData({
      ...personaData,
      maxLastSignInAt: maxLastSignInAt,
    })
  }, [maxLastSignInAt])

  const onClickSearchNeeds = async (hasFreeWord: boolean) => {
    if (hasFreeWord) deselectAll()
    else
      setPersonaData({
        ...personaData,
        freeWord: null,
      })

    await searchNeeds(hasFreeWord)
  }

  return (
    <>
      <Header>
        <HeaderLeftWrapper>
          <HeaderTabButton
            open={isDisplayForm}
            onClick={() => setIsDisplayForm(true)}
          >
            <NeedsSearchIcon />
            条件を設定して検索
          </HeaderTabButton>
        </HeaderLeftWrapper>
        <HeaderRightWrapper>
          <HeaderTabButton small={true} onClick={() => router.push(KEEP)}>
            <StyledNeedsAuthorizerIcon />
            <label>保存した決裁者一覧</label>
            <TooltipWrapper
              yValue="-40px"
              xValue={`0px`}
              tooltipStyle={readTooltipStyle}
              tooltipNode={<>保存した決裁者一覧</>}
            >
              <StyledNeedsAuthorizerXIcon />
            </TooltipWrapper>
          </HeaderTabButton>
          <HeaderTabButton
            small={true}
            open={!isDisplayForm}
            onClick={() => setIsDisplayForm(false)}
          >
            <StyledNeedsSavedSearchIcon />
            <label>保存済みの検索条件</label>
            <TooltipWrapper
              yValue="-40px"
              xValue={`0px`}
              tooltipStyle={readTooltipStyle}
              tooltipNode={<>保存済みの検索条件</>}
            >
              <StyledNeedsSavedSearchXIcon />
            </TooltipWrapper>
          </HeaderTabButton>
          <SearchFreeWordInputArea>
            <SearchFreeWordInput
              placeholder="フリーワードで検索"
              value={personaData?.freeWord ?? ''}
              onKeyPress={(event) => onKeyPress(event)}
              onChange={({ target: { value } }) =>
                setPersonaData({
                  ...personaData,
                  freeWord: value,
                })
              }
            />
            <StyledNeedsFreeSearchIcon
              onClick={() => onClickSearchNeeds(true)}
            />
          </SearchFreeWordInputArea>
        </HeaderRightWrapper>
      </Header>
      <PersonaMainContainer>
        <FormPersonaMainContainer>
          {isDisplayForm ? (
            <PersonaBoxComponent
              pageType="need"
              role={user.plan.role}
              masterData={masterData}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              checkedBusinessTypes={checkedBusinessTypes}
              setCheckedBusinessTypes={setCheckedBusinessTypes}
              checkedNumberOfEmployees={checkedNumberOfEmployees}
              setCheckedNumberOfEmployees={setCheckedNumberOfEmployees}
              checkedNeeds={checkedNeeds}
              setCheckedNeeds={setCheckedNeeds}
              checkedSolutions={checkedSolutions}
              setCheckedSolutions={setCheckedSolutions}
              checkedPrefectures={checkedPrefectures}
              setCheckedPrefectures={setCheckedPrefectures}
              checkedPositionNumberOptions={checkedPositionNumberOptions}
              setCheckedPositionNumberOptions={setCheckedPositionNumberOptions}
              checkedCapitalNumberOptions={checkedCapitalNumberOptions}
              setCheckedCapitalNumberOptions={setCheckedCapitalNumberOptions}
              persona={personaData}
              setPersona={setPersonaData}
              checkedFoundedAts={checkedFoundedAts}
              setCheckedFoundedAts={setCheckedFoundedAts}
              checkedBusinessObjects={checkedBusinessObjects}
              setCheckedBusinessObject={setCheckedBusinessObjects}
              checkedAdminAges={checkedAdminAges}
              setCheckedAdminAges={setCheckedAdminAges}
              minCreatedDate={minCreatedDate}
              setMinCreatedDate={setMinCreatedDate}
              maxCreatedDate={maxCreatedDate}
              setMaxCreatedDate={setMaxCreatedDate}
              minLastSignInAt={minLastSignInAt}
              setMinLastSignInAt={setMinLastSignInAt}
              maxLastSignInAt={maxLastSignInAt}
              setMaxLastSignInAt={setMaxLastSignInAt}
              masterDataRoleForAnalysis={masterDataRoleForAnalysis}
              checkedRoleForAnalysis={checkedRoleForAnalysis}
              setCheckedRoleForAnalysis={setCheckedRoleForAnalysis}
            />
          ) : (
            <SelectSavedSearchTabContainer>
              <p className="text">
                保存した検索条件から検索を行うことができます。
                <br />
                またペルソナ機能で設定した条件が自動的に保存された検索条件として表示されます。
              </p>
              <CompanySearchConditions>
                {searchCompanySearchConditionsData && (
                  <>
                    {searchCompanySearchConditionsData.map((item) => {
                      return (
                        <CompanySearchConditionsItem key={item.id}>
                          <p className={'text2'}>
                            <span>{item.title}</span>
                            <span
                              className={
                                'company-search-conditions-item-detail'
                              }
                              onClick={() =>
                                onClickSearchCompanySearchConditionsData(item)
                              }
                            >
                              設定中の項目を表示
                            </span>
                          </p>
                          <SetCompanySearchConditionButton
                            onClick={() =>
                              setSearchCompanySearchConditionsData(item)
                            }
                          >
                            検索項目にセットする
                          </SetCompanySearchConditionButton>
                          <DeleteCompanySearchConditionButton
                            onClick={() =>
                              deleteSearchCompanySearchConditionsData(item)
                            }
                          >
                            <StyledTrashIcon />
                            削除する
                          </DeleteCompanySearchConditionButton>
                        </CompanySearchConditionsItem>
                      )
                    })}
                  </>
                )}
                {personaDataList && (
                  <>
                    {personaDataList.map((item, i) => {
                      return (
                        <CompanySearchConditionsItem key={item.id}>
                          <p className={'text2'}>
                            <span>{`ペルソナ ${i + 1}`}</span>
                            <span
                              className={
                                'company-search-conditions-item-detail'
                              }
                              onClick={() => onClickPersonaData(item)}
                            >
                              設定中の項目を表示
                            </span>
                          </p>
                          <SetCompanySearchConditionButton
                            onClick={() => setPersonaData(item)}
                          >
                            検索項目にセットする
                          </SetCompanySearchConditionButton>
                        </CompanySearchConditionsItem>
                      )
                    })}
                  </>
                )}
              </CompanySearchConditions>
            </SelectSavedSearchTabContainer>
          )}
        </FormPersonaMainContainer>
        <PersonasContentContainer>
          <PersonasContentHeader>
            <p>選択中の項目</p>
            <PersonasContentClear onClick={deselectAll}>
              選択項目をリセット
            </PersonasContentClear>
          </PersonasContentHeader>
          <PersonasContentWrapper>
            <PersonasContent
              personaData={personaData}
              setPersonaData={setPersonaData}
              checkedBusinessTypes={checkedBusinessTypes}
              setCheckedBusinessTypes={setCheckedBusinessTypes}
              checkedNumberOfEmployees={checkedNumberOfEmployees}
              setCheckedNumberOfEmployees={setCheckedNumberOfEmployees}
              checkedNeeds={checkedNeeds}
              setCheckedNeeds={setCheckedNeeds}
              checkedSolutions={checkedSolutions}
              setCheckedSolutions={setCheckedSolutions}
              checkedPrefectures={checkedPrefectures}
              setCheckedPrefectures={setCheckedPrefectures}
              checkedPositionNumberOptions={checkedPositionNumberOptions}
              setCheckedPositionNumberOptions={setCheckedPositionNumberOptions}
              checkedCapitalNumberOptions={checkedCapitalNumberOptions}
              setCheckedCapitalNumberOptions={setCheckedCapitalNumberOptions}
              checkedFoundedAts={checkedFoundedAts}
              setCheckedFoundedAts={setCheckedFoundedAts}
              checkedBusinessObjects={checkedBusinessObjects}
              setCheckedBusinessObjects={setCheckedBusinessObjects}
              checkedAdminAges={checkedAdminAges}
              setCheckedAdminAges={setCheckedAdminAges}
              checkedRoleForAnalysis={checkedRoleForAnalysis}
              setCheckedRoleForAnalysis={setCheckedRoleForAnalysis}
            />
          </PersonasContentWrapper>
          <PersonasCheckboxWrapper>
            <PersonasCheckboxDiv>
              <input
                id={'checkbox_isNew'}
                type="checkbox"
                onChange={({ target: { checked } }) => {
                  setPersonaData({
                    ...personaData,
                    isNew: checked,
                  })
                }}
                checked={personaData.isNew}
              />
              <label htmlFor={'checkbox_isNew'}>新着企業のみを表示する</label>
            </PersonasCheckboxDiv>
            <PersonasCheckboxDiv>
              <input
                id={'checkbox_noMessage'}
                type="checkbox"
                onChange={({ target: { checked } }) => {
                  setPersonaData({
                    ...personaData,
                    noMessage: checked,
                  })
                }}
                checked={personaData.noMessage}
              />
              <label htmlFor={'checkbox_noMessage'}>
                つながり・メッセージ送信済みのユーザーを除外
              </label>
            </PersonasCheckboxDiv>
            <PersonasCheckboxDiv>
              <input
                id={'checkbox_refuseMessage'}
                type="checkbox"
                onChange={({ target: { checked } }) => {
                  setPersonaData({
                    ...personaData,
                    refuseMessage: checked,
                  })
                }}
                checked={personaData.refuseMessage}
              />
              <label htmlFor={'checkbox_refuseMessage'}>
                メッセージ受信拒否設定企業を除外
              </label>
            </PersonasCheckboxDiv>
          </PersonasCheckboxWrapper>
          <SearchButton onClick={() => onClickSearchNeeds(false)}>
            検索する
          </SearchButton>
        </PersonasContentContainer>
      </PersonaMainContainer>
      {showPersonasContentModal && (
        <PersonasContentModal
          personaData={selectedPersonaData}
          close={() => setShowPersonasContentModal(false)}
        />
      )}
    </>
  )
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  width: 100%;
  height: 90px;
  text-align: center;
  padding: 0 6px;
`
const HeaderLeftWrapper = styled.div``

const HeaderRightWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const HeaderTabButton = styled.button<{ open?: boolean; small?: boolean }>`
  width: 176px;
  height: 42px;
  padding: 12px 24px;
  border: 1px solid ${orange};
  background-color: ${truewhite};
  color: ${orange};
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  margin: 0 10px;
  outline: none;

  ${({ open }) =>
    open &&
    css`
      background-color: ${orange};
      color: ${truewhite};

      svg {
        path {
          fill: ${truewhite};
        }
      }
    `};

  ${({ small }) =>
    small &&
    css`
      @media (max-width: 1200px) {
        width: 50px;
        padding: 0;
        label {
          display: none;
        }
      }
    `};

  svg {
    margin-right: 4px;
  }

  :hover {
    ${({ open }) =>
      !open &&
      css`
        background-color: ${orange};
        color: ${truewhite};

        svg {
          path {
            fill: ${truewhite};
          }
        }
      `};
  }
`

const StyledNeedsAuthorizerIcon = styled(NeedsAuthorizerIcon)`
  @media (max-width: 1200px) {
    display: none;
  }
`

const StyledNeedsAuthorizerXIcon = styled(NeedsAuthorizerXIcon)`
  display: none;
  @media (max-width: 1200px) {
    display: inline-block;
  }
`

const StyledNeedsSavedSearchIcon = styled(NeedsSavedSearchIcon)`
  @media (max-width: 1200px) {
    display: none;
  }
`

const StyledNeedsSavedSearchXIcon = styled(NeedsSavedSearchXIcon)`
  display: none;
  @media (max-width: 1200px) {
    display: inline-block;
  }
`

const StyledNeedsFreeSearchIcon = styled(NeedsFreeSearchIcon)`
  cursor: pointer;
`

const SearchFreeWordInputArea = styled.div`
  width: 235px;
  height: 42px;
  display: inline;
  position: relative;
  top: 1px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ff6c00;
  border-radius: 5px;
  padding: 10px 10px 10px 20px;
  white-space: nowrap;
  margin-left: 10px;
  text-align: left;
`

const SearchFreeWordInput = styled.input`
  transform: translateY(-5px);
  border: none;
  width: 160px;
  margin-right: 10px;
  &::placeholder {
    color: #ff6c00;
    opacity: 0.5;
  }
`

const PersonaMainContainer = styled.div`
  width: 100%;
  display: flex;
  overflow: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
  padding: 0 6px 12px;
`

const FormPersonaMainContainer = styled.div`
  display: flex;
  height: 500px;
  box-shadow: 3px 6px 10px rgb(59 57 72 / 25%);
  border-radius: 10px;
  background: ${truewhite};
  width: 60%;
  min-width: 540px;
`

const PersonasContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 500px;
  box-shadow: 3px 6px 10px rgb(59 57 72 / 25%);
  border-radius: 10px;
  background: ${truewhite};
  width: 39%;
  min-width: 340px;
  padding: 20px;
  margin-left: 8px;
`

const PersonasContentHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #cecdd1;
  font-weight: ${fontWeightBold};
  margin-bottom: 10px;
`

const PersonasContentClear = styled.button`
  color: #cecdd1;
  border: 1px solid #393e4c40;
  background: #fff;
  padding: 4px 15px;
  box-shadow: none;
  font-size: 12px;
  font-weight: ${fontWeightBold};
  line-height: 17px;
  letter-spacing: -0.2px;
  border-radius: 3px;
  outline: none;
  text-align: center;

  :hover {
    color: ${orange};
    border: 1px solid ${orange};
  }
`

const PersonasContentWrapper = styled.div`
  width: 100%;
  height: 290px;
  display: flex;
  flex-wrap: wrap;
  overflow: scroll;
  ::-webkit-scrollbar:horizontal {
    height: 4px;
  }
  border-bottom: 1px solid #393e4c40;
`

const PersonasCheckboxWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: left;
`

const PersonasCheckboxDiv = styled.div`
  margin-bottom: 4px;

  input[type='checkbox'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    -ms-appearance: none;
    -o-appearance: none;
    appearance: none;
    position: relative;
    right: 0;
    bottom: 0;
    left: 0;
    height: 18px;
    width: 18px;
    color: ${orange};
    cursor: pointer;
    display: inline-block;
    outline: none;
    border-radius: 3px;
  }

  input[type='checkbox']:before,
  input[type='checkbox']:after {
    position: absolute;
    content: '';
    background: ${orange};
  }
  input[type='checkbox']:before {
    left: 2px;
    top: 6px;
    width: 0;
    height: 2px;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    -moz-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    -o-transform: rotate(45deg);
  }
  input[type='checkbox']:after {
    right: 9px;
    bottom: 3px;
    width: 2px;
    height: 0;
    transform: rotate(40deg);
    -webkit-transform: rotate(40deg);
    -moz-transform: rotate(40deg);
    -ms-transform: rotate(40deg);
    -o-transform: rotate(40deg);
  }
  input[type='checkbox']:checked:before {
    left: 1px;
    top: 9px;
    width: 6px;
    height: 3px;
  }
  input[type='checkbox']:checked:after {
    right: 4px;
    bottom: 0px;
    width: 3px;
    height: 14px;
  }
  input[type='checkbox']:indeterminate:before,
  input[type='checkbox']:indeterminate:after {
    width: 7px;
    height: 2px;
    transform: rotate(0);
    -webkit-transform: rotate(0);
    -moz-transform: rotate(0);
    -ms-transform: rotate(0);
    -o-transform: rotate(0);
  }
  input[type='checkbox']:indeterminate:before {
    left: 1px;
    top: 7px;
  }
  input[type='checkbox']:indeterminate:after {
    right: 1px;
    bottom: 7px;
  }
  input[type='checkbox'] {
    border: 1px solid #e2e2e2;
  }
  input[type='checkbox']:checked,
  input[type='checkbox']:indeterminate {
    border: 1px solid #e2e2e2;
    background: ${truewhite};
  }

  label {
    font-size: 12px;
    font-weight: ${fontWeightBold};
    color: #929292;
    line-height: 17px;
    letter-spacing: -0.2px;
    margin-left: 11px;
  }
`

const SearchButton = styled(LargeButton)`
  color: ${truewhite};
  background: ${safetyOrange};
  padding: 10px 0;
  width: 160px;
`

const SelectSavedSearchTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 500px;
  overflow-y: auto;
  padding: 20px 80px;
  font-size: 14px;
  font-weight: bold;

  .text {
    color: #bbbbbb;
    margin: 0;
  }
`

const CompanySearchConditions = styled.div``

const CompanySearchConditionsItem = styled.div`
  .text2 {
    color: #3b3948;
    font-weight: bold;
    margin-top: 30px;
    margin-bottom: 10px;
  }

  .company-search-conditions-item-detail {
    color: ${orange};
    opacity: 0.35;
    font-weight: bold;
    cursor: pointer;
    margin-left: 5px;
  }
`

const SetCompanySearchConditionButton = styled.button`
  width: 200px;
  line-height: 1.4;
  padding: 10px 0;
  border: 1px solid ${orange};
  background: rgba(255, 108, 0, 0.1);
  color: ${orange};
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  text-align: center;
  margin-right: 10px;
`

const DeleteCompanySearchConditionButton = styled.button`
  width: 200px;
  line-height: 1.4;
  padding: 10px 0;
  border: 1px solid #393e4c40;
  background: ${truewhite};
  color: #393e4c40;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  text-align: center;

  :hover {
    color: ${orange};
    border: 1px solid ${orange};

    svg {
      path {
        fill: ${orange};
      }
    }
  }
`

const StyledTrashIcon = styled(TrashIcon)`
  margin-right: 3px;
  transform: translateY(1px);
`

const readTooltipStyle = css`
  border: 1px solid ${orange};
  background-color: ${truewhite};
  color: ${orange};
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 3px 6px rgb(0 0 0 / 10%);
  border-radius: 5px;
  outline: none;

  display: none;
  @media (max-width: 1200px) {
    display: inline-block;
  }
`
